import { useState, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { BarChart3, TrendingUp, PieChart, Users, CheckSquare, Wrench, ArrowRightLeft, Target, Save, Trash2, Wand2, Filter, Clock, Download } from "lucide-react"
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RePieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area } from "recharts"
import { useReport, useSavedReports, useSaveReport, useDeleteSavedReport, useReportExport } from "@/hooks/use-reports"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { toast } from "sonner"

const REPORT_TYPES = [
  { key: "pipeline", label: "Pipeline sprzedażowy", icon: BarChart3, description: "Transakcje wg etapów" },
  { key: "revenue", label: "Przychody", icon: TrendingUp, description: "Przychody w czasie" },
  { key: "won_lost", label: "Wygrane/Przegrane", icon: PieChart, description: "Stosunek wygranych do przegranych" },
  { key: "activity", label: "Aktywności", icon: Target, description: "Aktywności wg typu i użytkownika" },
  { key: "deals_by_owner", label: "Transakcje wg opiekuna", icon: Users, description: "Transakcje pogrupowane wg opiekuna" },
  { key: "conversion", label: "Konwersja etapów", icon: ArrowRightLeft, description: "Współczynniki konwersji między etapami" },
  { key: "tasks", label: "Zadania", icon: CheckSquare, description: "Statystyki zadań" },
  { key: "service", label: "Serwis", icon: Wrench, description: "Statystyki zgłoszeń serwisowych" },
  { key: "funnel", label: "Lejek sprzedaży", icon: Filter, description: "Szczegółowy lejek z konwersją między etapami" },
  { key: "source", label: "Źródła pozyskania", icon: PieChart, description: "Dystrybucja źródeł pozyskania klientów" },
  { key: "time_analysis", label: "Analiza czasowa", icon: Clock, description: "Trendy sprzedaży i czas trwania transakcji" },
]

const CHART_COLORS = [
  "var(--primary)", "#14B8A6", "#F59E0B", "#EF4444", "#8B5CF6",
  "#EC4899", "#06B6D4", "#84CC16", "#F97316", "#6366F1",
]

function formatCurrency(val: number) {
  return new Intl.NumberFormat("pl-PL", { style: "currency", currency: "PLN", maximumFractionDigits: 0 }).format(val)
}

export function ReportsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [selectedType, setSelectedType] = useState("pipeline")
  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")
  const [isExporting, setIsExporting] = useState(false)

  const params = useMemo(() => {
    const p: Record<string, string> = {}
    if (dateFrom) p.date_from = dateFrom
    if (dateTo) p.date_to = dateTo
    return p
  }, [dateFrom, dateTo])

  const { data: reportData, isLoading } = useReport(selectedType, params)
  const { data: savedData } = useSavedReports()
  const saveMutation = useSaveReport()
  const deleteSavedMutation = useDeleteSavedReport()
  const exportReport = useReportExport()

  const report = reportData?.data
  const savedReports = savedData?.data ?? []

  const handleSave = async () => {
    const name = prompt("Nazwa raportu:")
    if (!name) return
    await saveMutation.mutateAsync({ name, report_type: selectedType, config: params })
    toast.success("Raport zapisany")
  }

  const handleExport = async () => {
    setIsExporting(true)
    try {
      await exportReport(selectedType, params)
      toast.success("Plik CSV pobrany")
    } catch {
      toast.error("Błąd eksportu raportu")
    } finally {
      setIsExporting(false)
    }
  }

  const handleLoadSaved = (saved: { report_type: string; config: Record<string, unknown> }) => {
    setSelectedType(saved.report_type)
    if (saved.config.date_from) setDateFrom(saved.config.date_from as string)
    if (saved.config.date_to) setDateTo(saved.config.date_to as string)
  }

  const selectedConfig = REPORT_TYPES.find(r => r.key === selectedType)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">{t("reports.title", "Raporty")}</h1>
          <p className="text-sm text-muted-foreground">{t("reports.subtitle", "Analizuj dane sprzedażowe, aktywności i serwis")}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate("/reports/custom")}>
            <Wand2 size={14} /> {t("reports.builder", "Kreator")}
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={isExporting || isLoading}>
            <Download size={14} /> {isExporting ? t("reports.exporting", "Eksportowanie...") : t("reports.exportCsv", "Eksport CSV")}
          </Button>
          <Button variant="outline" size="sm" onClick={handleSave}>
            <Save size={14} /> {t("reports.saveReport", "Zapisz raport")}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-6">
        {/* Left sidebar: report type selector */}
        <div className="space-y-4">
          <div className="space-y-1">
            {REPORT_TYPES.map(rt => {
              const Icon = rt.icon
              const isActive = selectedType === rt.key
              return (
                <button
                  key={rt.key}
                  onClick={() => setSelectedType(rt.key)}
                  className={`flex items-center gap-2.5 w-full rounded-md px-3 py-2 text-left text-sm transition-colors ${isActive ? "bg-primary/[0.08] text-primary font-semibold" : "text-muted-foreground"}`}
                >
                  <Icon size={16} />
                  <span className="flex-1">{rt.label}</span>
                </button>
              )
            })}
          </div>

          {/* Date filter */}
          <div className="surface-card p-3 space-y-2">
            <p className="text-xs font-semibold text-muted-foreground">{t("reports.dateRange", "Zakres dat")}</p>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="w-full rounded-md border border-input bg-transparent px-2 py-1.5 text-xs text-foreground outline-none"
              placeholder="Od"
            />
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="w-full rounded-md border border-input bg-transparent px-2 py-1.5 text-xs text-foreground outline-none"
              placeholder="Do"
            />
            {(dateFrom || dateTo) && (
              <button
                onClick={() => { setDateFrom(""); setDateTo("") }}
                className="text-xs underline text-primary"
              >
                {t("reports.clearFilter", "Wyczyść filtr")}
              </button>
            )}
          </div>

          {/* Saved reports */}
          {savedReports.length > 0 && (
            <div className="surface-card p-3 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground">{t("reports.savedReports", "Zapisane raporty")}</p>
              <div className="space-y-1">
                {savedReports.map(sr => (
                  <div key={sr.id} className="flex items-center gap-1.5">
                    <button
                      onClick={() => handleLoadSaved(sr)}
                      className="flex-1 truncate rounded-md px-2 py-1 text-xs text-left transition-colors hover:bg-muted/30 text-foreground"
                    >
                      {sr.name}
                    </button>
                    <button
                      onClick={async () => {
                        await deleteSavedMutation.mutateAsync(sr.id)
                        toast.success("Raport usunięty")
                      }}
                      className="p-0.5 rounded transition-colors hover:bg-muted/30 text-muted-foreground"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Main content: report viewer */}
        <div className="col-span-3">
          <div className="surface-card p-6 space-y-6">
            <div className="flex items-center gap-3">
              {selectedConfig && <selectedConfig.icon size={20} className="text-primary" />}
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  {selectedConfig?.label}
                </h2>
                <p className="text-xs text-muted-foreground">{selectedConfig?.description}</p>
              </div>
              {reportData?.generated_at && (
                <Badge variant="secondary" className="ml-auto text-[10px]">
                  {t("reports.generatedAt", "Wygenerowano")}: {new Date(reportData.generated_at).toLocaleString("pl-PL")}
                </Badge>
              )}
            </div>

            {isLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-64 w-full" />
              </div>
            ) : report ? (
              <ReportChart type={selectedType} data={report} />
            ) : (
              <p className="text-center py-16 text-sm text-muted-foreground">
                {t("reports.noData", "Brak danych dla wybranego raportu")}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function ReportChart({ type, data }: { type: string; data: Record<string, unknown> }) {
  switch (type) {
    case "pipeline":
      return <PipelineChart data={data} />
    case "revenue":
      return <RevenueChart data={data} />
    case "won_lost":
      return <WonLostChart data={data} />
    case "activity":
      return <ActivityChart data={data} />
    case "deals_by_owner":
      return <DealsByOwnerChart data={data} />
    case "conversion":
      return <ConversionChart data={data} />
    case "tasks":
      return <TasksChart data={data} />
    case "service":
      return <ServiceChart data={data} />
    case "funnel":
      return <FunnelChart data={data} />
    case "source":
      return <SourceChart data={data} />
    case "time_analysis":
      return <TimeAnalysisChart data={data} />
    default:
      return <pre className="text-xs overflow-auto text-muted-foreground">{JSON.stringify(data, null, 2)}</pre>
  }
}

// ── Pipeline Report ──

function PipelineChart({ data }: { data: Record<string, unknown> }) {
  const phases = (data.phases as Array<{ name: string; count: number; value: number; weighted_value: number; color: string }>) ?? []
  const totals = data.totals as { count: number; value: number; weighted: number } | undefined

  return (
    <div className="space-y-4">
      {totals && (
        <div className="grid grid-cols-3 gap-4">
          <NumberCard label="Łącznie transakcji" value={totals.count} />
          <NumberCard label="Wartość" value={formatCurrency(totals.value)} />
          <NumberCard label="Wartość ważona" value={formatCurrency(totals.weighted)} />
        </div>
      )}
      <div className="h-[320px]">
        <ResponsiveContainer>
          <BarChart data={phases}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} />
            <YAxis tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} />
            <Tooltip formatter={(val: number) => formatCurrency(val)} contentStyle={{ fontSize: 12 }} />
            <Bar dataKey="value" name="Wartość" radius={[4, 4, 0, 0]}>
              {phases.map((p, i) => (
                <Cell key={i} fill={p.color || CHART_COLORS[i % CHART_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

// ── Revenue Report ──

function RevenueChart({ data }: { data: Record<string, unknown> }) {
  const periods = (data.periods as Array<{ period: string; revenue: number; deal_count: number }>) ?? []
  const totalRevenue = data.total_revenue as number | undefined

  return (
    <div className="space-y-4">
      {totalRevenue != null && (
        <div className="grid grid-cols-2 gap-4">
          <NumberCard label="Łączny przychód" value={formatCurrency(totalRevenue)} />
          <NumberCard label="Transakcji zamkniętych" value={periods.reduce((s, p) => s + p.deal_count, 0)} />
        </div>
      )}
      <div className="h-[320px]">
        <ResponsiveContainer>
          <LineChart data={periods}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="period" tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} />
            <YAxis tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} />
            <Tooltip formatter={(val: number) => formatCurrency(val)} contentStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="revenue" name="Przychód" stroke="var(--primary)" strokeWidth={2} dot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

// ── Won/Lost Report ──

function WonLostChart({ data }: { data: Record<string, unknown> }) {
  const totals = data.totals as { won_count: number; won_value: number; lost_count: number; lost_value: number; win_rate: number } | undefined
  const periods = (data.periods as Array<{ period: string; won_count: number; lost_count: number; won_value: number; lost_value: number }>) ?? []

  const pieData = totals ? [
    { name: "Wygrane", value: totals.won_count },
    { name: "Przegrane", value: totals.lost_count },
  ] : []

  return (
    <div className="space-y-4">
      {totals && (
        <div className="grid grid-cols-4 gap-4">
          <NumberCard label="Wygrane" value={totals.won_count} color="#22C55E" />
          <NumberCard label="Przegrane" value={totals.lost_count} color="#EF4444" />
          <NumberCard label="Wartość wygranych" value={formatCurrency(totals.won_value)} color="#22C55E" />
          <NumberCard label="Win rate" value={`${totals.win_rate.toFixed(0)}%`} />
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        <div className="h-[280px]">
          <ResponsiveContainer>
            <RePieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
                <Cell fill="#22C55E" />
                <Cell fill="#EF4444" />
              </Pie>
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Legend />
            </RePieChart>
          </ResponsiveContainer>
        </div>
        <div className="h-[280px]">
          <ResponsiveContainer>
            <BarChart data={periods}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="period" tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} />
              <YAxis tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} />
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Bar dataKey="won_count" name="Wygrane" fill="#22C55E" radius={[4, 4, 0, 0]} />
              <Bar dataKey="lost_count" name="Przegrane" fill="#EF4444" radius={[4, 4, 0, 0]} />
              <Legend />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}

// ── Activity Report ──

function ActivityChart({ data }: { data: Record<string, unknown> }) {
  const byType = (data.by_type as Array<{ type_name: string; count: number }>) ?? []
  const byUser = (data.by_user as Array<{ user_name: string; count: number }>) ?? []
  const total = data.total as number | undefined

  return (
    <div className="space-y-4">
      {total != null && (
        <NumberCard label="Łącznie aktywności" value={total} />
      )}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <h3 className="text-sm font-semibold mb-2 text-foreground">Wg typu</h3>
          <div className="h-[260px]">
            <ResponsiveContainer>
              <BarChart data={byType} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis type="number" tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} />
                <YAxis dataKey="type_name" type="category" tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} width={100} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="count" name="Ilość" fill="var(--primary)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div>
          <h3 className="text-sm font-semibold mb-2 text-foreground">Wg użytkownika</h3>
          <div className="h-[260px]">
            <ResponsiveContainer>
              <BarChart data={byUser} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis type="number" tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} />
                <YAxis dataKey="user_name" type="category" tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} width={100} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="count" name="Ilość" fill="#14B8A6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Deals by Owner ──

function DealsByOwnerChart({ data }: { data: Record<string, unknown> }) {
  const owners = (data.owners as Array<{ name: string; count: number; value: number; won_count: number; won_value: number }>) ?? []

  return (
    <div className="space-y-4">
      <div className="h-[320px]">
        <ResponsiveContainer>
          <BarChart data={owners}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} />
            <YAxis tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} />
            <Tooltip formatter={(val: number) => formatCurrency(val)} contentStyle={{ fontSize: 12 }} />
            <Bar dataKey="value" name="Wartość ogółem" fill="var(--primary)" radius={[4, 4, 0, 0]} />
            <Bar dataKey="won_value" name="Wartość wygranych" fill="#22C55E" radius={[4, 4, 0, 0]} />
            <Legend />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {/* Table */}
      <div className="overflow-auto">
        <table className="w-full text-sm text-foreground">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">Opiekun</th>
              <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground">Transakcje</th>
              <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground">Wartość</th>
              <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground">Wygrane</th>
              <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground">Wartość wygranych</th>
            </tr>
          </thead>
          <tbody>
            {owners.map((o, i) => (
              <tr key={i} className="border-b">
                <td className="py-2 px-3">{o.name}</td>
                <td className="py-2 px-3 text-right">{o.count}</td>
                <td className="py-2 px-3 text-right font-mono tabular-nums">{formatCurrency(o.value)}</td>
                <td className="py-2 px-3 text-right">{o.won_count}</td>
                <td className="py-2 px-3 text-right font-mono tabular-nums">{formatCurrency(o.won_value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Conversion Report ──

function ConversionChart({ data }: { data: Record<string, unknown> }) {
  const phases = (data.phases as Array<{ from: string; to: string; conversion_rate: number; avg_days: number }>) ?? []

  return (
    <div className="space-y-4">
      <div className="overflow-auto">
        <table className="w-full text-sm text-foreground">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">Z etapu</th>
              <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">Do etapu</th>
              <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground">Konwersja</th>
              <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground">Śr. dni</th>
            </tr>
          </thead>
          <tbody>
            {phases.map((p, i) => (
              <tr key={i} className="border-b">
                <td className="py-2 px-3">{p.from}</td>
                <td className="py-2 px-3">{p.to}</td>
                <td className="py-2 px-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <div className="h-2 rounded-full bg-primary" style={{ width: `${Math.min(p.conversion_rate, 100)}%`, minWidth: 4 }} />
                    <span className="font-mono text-xs tabular-nums">{p.conversion_rate.toFixed(0)}%</span>
                  </div>
                </td>
                <td className="py-2 px-3 text-right font-mono tabular-nums">{p.avg_days.toFixed(1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Tasks Report ──

function TasksChart({ data }: { data: Record<string, unknown> }) {
  const completed = (data.completed as number) ?? 0
  const overdue = (data.overdue as number) ?? 0
  const pending = (data.pending as number) ?? 0
  const byAssignee = (data.by_assignee as Array<{ name: string; completed: number; pending: number }>) ?? []

  const pieData = [
    { name: "Ukończone", value: completed },
    { name: "Zaległe", value: overdue },
    { name: "W toku", value: pending },
  ].filter(d => d.value > 0)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <NumberCard label="Ukończone" value={completed} color="#22C55E" />
        <NumberCard label="Zaległe" value={overdue} color="#EF4444" />
        <NumberCard label="W toku" value={pending} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="h-[280px]">
          <ResponsiveContainer>
            <RePieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
                <Cell fill="#22C55E" />
                <Cell fill="#EF4444" />
                <Cell fill="var(--primary)" />
              </Pie>
              <Tooltip contentStyle={{ fontSize: 12 }} />
              <Legend />
            </RePieChart>
          </ResponsiveContainer>
        </div>
        {byAssignee.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold mb-2 text-foreground">Wg przypisanego</h3>
            <div className="h-[260px]">
              <ResponsiveContainer>
                <BarChart data={byAssignee} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis type="number" tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} width={100} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="completed" name="Ukończone" fill="#22C55E" stackId="a" />
                  <Bar dataKey="pending" name="W toku" fill="var(--primary)" stackId="a" radius={[0, 4, 4, 0]} />
                  <Legend />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Service Report ──

function ServiceChart({ data }: { data: Record<string, unknown> }) {
  const byStatus = data.by_status as Record<string, number> | undefined
  const byPriority = data.by_priority as Record<string, number> | undefined
  const avgResolution = data.avg_resolution_hours as number | undefined
  const totalPartsCost = data.total_parts_cost as number | undefined

  const statusData = byStatus ? [
    { name: "Otwarte", value: byStatus.open ?? 0 },
    { name: "Rozwiązane", value: byStatus.resolved ?? 0 },
    { name: "Zamknięte", value: byStatus.closed ?? 0 },
  ].filter(d => d.value > 0) : []

  const priorityData = byPriority ? Object.entries(byPriority).map(([key, val]) => ({
    name: key === "low" ? "Niski" : key === "medium" ? "Średni" : key === "high" ? "Wysoki" : "Pilny",
    value: val,
  })) : []

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <NumberCard label="Otwarte" value={byStatus?.open ?? 0} />
        <NumberCard label="Śr. czas rozwiązania" value={avgResolution != null ? `${avgResolution.toFixed(1)}h` : "--"} />
        <NumberCard label="Koszt części" value={totalPartsCost != null ? formatCurrency(totalPartsCost) : "--"} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <h3 className="text-sm font-semibold mb-2 text-foreground">Wg statusu</h3>
          <div className="h-[260px]">
            <ResponsiveContainer>
              <RePieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  <Cell fill="var(--primary)" />
                  <Cell fill="#22C55E" />
                  <Cell fill="#6B7280" />
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Legend />
              </RePieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div>
          <h3 className="text-sm font-semibold mb-2 text-foreground">Wg priorytetu</h3>
          <div className="h-[260px]">
            <ResponsiveContainer>
              <BarChart data={priorityData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} />
                <YAxis tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="value" name="Ilość" radius={[4, 4, 0, 0]}>
                  <Cell fill="#6B7280" />
                  <Cell fill="#3B82F6" />
                  <Cell fill="#F59E0B" />
                  <Cell fill="#EF4444" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Funnel Chart ──

interface FunnelPhase {
  id: string
  name: string
  color: string
  sort_order: number
  count: number
  value: number
}

interface FunnelConversion {
  from: string
  to: string
  rate: number
}

interface FunnelData {
  deal_type_id: string
  deal_type_name: string
  phases: FunnelPhase[]
  conversions: FunnelConversion[]
  won: { count: number; value: number }
  overall_conversion: number
  total_count: number
  total_value: number
}

function FunnelChart({ data }: { data: Record<string, unknown> }) {
  const funnels = (data.funnels as FunnelData[]) ?? []

  if (funnels.length === 0) {
    return <p className="text-center py-8 text-sm text-muted-foreground">Brak danych lejka sprzedaży</p>
  }

  return (
    <div className="space-y-8">
      {funnels.map((funnel) => (
        <div key={funnel.deal_type_id} className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">
            Pipeline: {funnel.deal_type_name}
          </h3>

          {/* Summary cards */}
          <div className="grid grid-cols-4 gap-4">
            <NumberCard label="Transakcje w lejku" value={funnel.total_count} />
            <NumberCard label="Wartość w lejku" value={formatCurrency(funnel.total_value)} />
            <NumberCard label="Wygrane" value={funnel.won.count} color="#22C55E" />
            <NumberCard label="Konwersja ogólna" value={`${funnel.overall_conversion}%`} />
          </div>

          {/* Funnel bar chart */}
          <div className="h-[320px]">
            <ResponsiveContainer>
              <BarChart data={funnel.phases} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis type="number" tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} />
                <YAxis
                  dataKey="name"
                  type="category"
                  tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                  width={120}
                />
                <Tooltip
                  formatter={(val: number, name: string) =>
                    name === "value" ? formatCurrency(val) : val
                  }
                  contentStyle={{ fontSize: 12 }}
                />
                <Bar dataKey="count" name="Ilość" radius={[0, 4, 4, 0]}>
                  {funnel.phases.map((phase, i) => (
                    <Cell key={i} fill={phase.color || CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Conversion rates between phases */}
          {funnel.conversions.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground mb-2">Konwersja między etapami</h4>
              <div className="flex flex-wrap gap-2">
                {funnel.conversions.map((c, i) => (
                  <div key={i} className="surface-card px-3 py-2 flex items-center gap-2 text-xs">
                    <span className="text-foreground">{c.from}</span>
                    <span className="text-muted-foreground">&rarr;</span>
                    <span className="text-foreground">{c.to}</span>
                    <Badge
                      variant="secondary"
                      className={`text-[10px] ${c.rate >= 50 ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" : c.rate >= 25 ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"}`}
                    >
                      {c.rate}%
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Phase detail table */}
          <div className="overflow-auto">
            <table className="w-full text-sm text-foreground">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">Etap</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground">Ilość</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground">Wartość</th>
                </tr>
              </thead>
              <tbody>
                {funnel.phases.map((phase, i) => (
                  <tr key={i} className="border-b">
                    <td className="py-2 px-3 flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: phase.color || CHART_COLORS[i % CHART_COLORS.length] }} />
                      {phase.name}
                    </td>
                    <td className="py-2 px-3 text-right font-mono tabular-nums">{phase.count}</td>
                    <td className="py-2 px-3 text-right font-mono tabular-nums">{formatCurrency(phase.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Source Analytics Chart ──

interface SourceItem {
  source_id: string | null
  name: string
  count: number
  percentage: number
}

interface SourceDealItem {
  source_id: string | null
  name: string
  deal_count: number
  deal_value: number
  won_count: number
  won_value: number
}

interface SourceMonthly {
  period: string
  count: number
}

function SourceChart({ data }: { data: Record<string, unknown> }) {
  const bySource = (data.by_source as SourceItem[]) ?? []
  const dealsBySource = (data.deals_by_source as SourceDealItem[]) ?? []
  const monthly = (data.monthly as SourceMonthly[]) ?? []
  const total = (data.total as number) ?? 0

  const pieData = bySource.map(s => ({
    name: s.name,
    value: s.count,
  }))

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <NumberCard label="Łącznie firm" value={total} />
        <NumberCard label="Źródeł" value={bySource.length} />
        <NumberCard label="Transakcji powiązanych" value={dealsBySource.reduce((s, d) => s + d.deal_count, 0)} />
      </div>

      {/* Pie chart + table */}
      <div className="grid grid-cols-2 gap-6">
        <div>
          <h3 className="text-sm font-semibold mb-2 text-foreground">Dystrybucja źródeł</h3>
          <div className="h-[300px]">
            <ResponsiveContainer>
              <RePieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  innerRadius={50}
                  label={({ name, percent }) => `${name} (${((percent ?? 0) * 100).toFixed(0)}%)`}
                  labelLine={{ strokeWidth: 1 }}
                >
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12 }} />
              </RePieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div>
          <h3 className="text-sm font-semibold mb-2 text-foreground">Szczegóły źródeł</h3>
          <div className="overflow-auto max-h-[300px]">
            <table className="w-full text-sm text-foreground">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">Źródło</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground">Firmy</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground">Udział</th>
                </tr>
              </thead>
              <tbody>
                {bySource.map((s, i) => (
                  <tr key={i} className="border-b">
                    <td className="py-2 px-3 flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                      {s.name}
                    </td>
                    <td className="py-2 px-3 text-right font-mono tabular-nums">{s.count}</td>
                    <td className="py-2 px-3 text-right font-mono tabular-nums">{s.percentage}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Deals by source */}
      {dealsBySource.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2 text-foreground">Transakcje wg źródła</h3>
          <div className="h-[280px]">
            <ResponsiveContainer>
              <BarChart data={dealsBySource}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} />
                <YAxis tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} />
                <Tooltip formatter={(val: number) => formatCurrency(val)} contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="deal_value" name="Wartość transakcji" fill="var(--primary)" radius={[4, 4, 0, 0]} />
                <Bar dataKey="won_value" name="Wartość wygranych" fill="#22C55E" radius={[4, 4, 0, 0]} />
                <Legend />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Monthly trend */}
      {monthly.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2 text-foreground">Trend miesięczny - nowe firmy</h3>
          <div className="h-[220px]">
            <ResponsiveContainer>
              <AreaChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="period" tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} />
                <YAxis tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="count" name="Nowe firmy" stroke="var(--primary)" fill="var(--primary)" fillOpacity={0.1} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Time Analysis Chart ──

interface TimeMonthly {
  period: string
  won_count: number
  won_value: number
  lost_count: number
  lost_value: number
}

interface TimeDuration {
  avg_days: number
  min_days: number
  max_days: number
  median_days: number
}

interface TimeDurationByMonth {
  period: string
  avg_days: number
  count: number
}

interface TimeRevenueTrend {
  period: string
  revenue: number
  deal_count: number
}

function TimeAnalysisChart({ data }: { data: Record<string, unknown> }) {
  const monthly = (data.monthly as TimeMonthly[]) ?? []
  const duration = data.duration as TimeDuration | undefined
  const durationByMonth = (data.duration_by_month as TimeDurationByMonth[]) ?? []
  const revenueTrend = (data.revenue_trend as TimeRevenueTrend[]) ?? []
  const totals = data.totals as { won_count: number; lost_count: number; total_revenue: number } | undefined

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      {totals && (
        <div className="grid grid-cols-4 gap-4">
          <NumberCard label="Wygrane transakcje" value={totals.won_count} color="#22C55E" />
          <NumberCard label="Przegrane transakcje" value={totals.lost_count} color="#EF4444" />
          <NumberCard label="Łączny przychód" value={formatCurrency(totals.total_revenue)} />
          <NumberCard label="Śr. czas transakcji" value={duration ? `${duration.avg_days} dni` : "--"} />
        </div>
      )}

      {/* Won/Lost monthly trend */}
      {monthly.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2 text-foreground">Wygrane i przegrane w czasie</h3>
          <div className="h-[300px]">
            <ResponsiveContainer>
              <BarChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="period" tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} />
                <YAxis tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="won_count" name="Wygrane" fill="#22C55E" radius={[4, 4, 0, 0]} />
                <Bar dataKey="lost_count" name="Przegrane" fill="#EF4444" radius={[4, 4, 0, 0]} />
                <Legend />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Revenue trend */}
      {revenueTrend.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2 text-foreground">Trend przychodów</h3>
          <div className="h-[280px]">
            <ResponsiveContainer>
              <AreaChart data={revenueTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="period" tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} />
                <YAxis tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} />
                <Tooltip formatter={(val: number) => formatCurrency(val)} contentStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="revenue" name="Przychód" stroke="var(--primary)" fill="var(--primary)" fillOpacity={0.1} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Deal duration */}
      <div className="grid grid-cols-2 gap-6">
        {/* Duration stats */}
        {duration && (
          <div>
            <h3 className="text-sm font-semibold mb-2 text-foreground">Czas trwania transakcji (dni)</h3>
            <div className="grid grid-cols-2 gap-3">
              <NumberCard label="Średni" value={`${duration.avg_days} dni`} />
              <NumberCard label="Mediana" value={`${duration.median_days} dni`} />
              <NumberCard label="Minimalny" value={`${duration.min_days} dni`} />
              <NumberCard label="Maksymalny" value={`${duration.max_days} dni`} />
            </div>
          </div>
        )}

        {/* Duration by month trend */}
        {durationByMonth.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold mb-2 text-foreground">Średni czas transakcji wg miesiąca</h3>
            <div className="h-[200px]">
              <ResponsiveContainer>
                <LineChart data={durationByMonth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="period" tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} />
                  <YAxis tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} />
                  <Tooltip
                    formatter={(val: number) => `${val} dni`}
                    contentStyle={{ fontSize: 12 }}
                  />
                  <Line type="monotone" dataKey="avg_days" name="Śr. dni" stroke="#F59E0B" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Monthly detail table */}
      {monthly.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2 text-foreground">Szczegóły miesięczne</h3>
          <div className="overflow-auto">
            <table className="w-full text-sm text-foreground">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground">Okres</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground">Wygrane</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground">Wartość wygranych</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground">Przegrane</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground">Wartość przegranych</th>
                </tr>
              </thead>
              <tbody>
                {monthly.map((m, i) => (
                  <tr key={i} className="border-b">
                    <td className="py-2 px-3">{m.period}</td>
                    <td className="py-2 px-3 text-right font-mono tabular-nums text-green-600">{m.won_count}</td>
                    <td className="py-2 px-3 text-right font-mono tabular-nums">{formatCurrency(m.won_value)}</td>
                    <td className="py-2 px-3 text-right font-mono tabular-nums text-red-500">{m.lost_count}</td>
                    <td className="py-2 px-3 text-right font-mono tabular-nums">{formatCurrency(m.lost_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Number Card ──

function NumberCard({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <div className="surface-card p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold mt-1 tabular-nums" style={{ color: color || undefined }}>
        {typeof value === "number" ? <span className="text-foreground">{value}</span> : value}
      </p>
    </div>
  )
}
